import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Badge,
  Button,
  Spinner,
  Alert,
  Modal,
} from "react-bootstrap";
import "../css/workout-detail.css";
import { useAuth } from "../auth/AuthContext";
import { api } from "../services/api";
import { API_BASE_URL } from "../config";

const resolveCoverUrl = (u) => {
  if (!u) return `${API_BASE_URL}/uploads/covers/placeholder.jpg`;
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/uploads/")) return `${API_BASE_URL}${u}`;
  return u;
};

export default function WorkoutDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, token } = useAuth();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [buyLoading, setBuyLoading] = useState(false);
  const [buyError, setBuyError] = useState("");

  // ✅ checkout success modal
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);

  // ===== intercetta ?checkout=success =====
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      setCheckoutSuccess(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // ===== fetch workout =====
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        const safeSlug = (slug || "").replace(/\+/g, " ");
        const data = await api(
          `/api/workouts/slug/${encodeURIComponent(safeSlug)}`
        );
        if (!cancelled) setPlan(data);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setPlan(null);
          setErr("Unable to load workout.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const available = useMemo(
    () => !!plan && (plan.status || "").toLowerCase() === "available",
    [plan]
  );

  // ===== checkout =====
  async function handleBuy() {
    if (!available) return;

    if (!isAuthenticated || !user?.id) {
      alert("You must be registered and logged in to buy a workout.");
      navigate("/login");
      return;
    }

    setBuyError("");
    setBuyLoading(true);

    try {
      const payload = await api("/api/checkout/session", {
        method: "POST",
        token,
        body: {
          workoutId: plan.id,
          userId: user.id,
        },
      });

      if (!payload?.url) throw new Error("Missing checkout URL");
      window.location.href = payload.url;
    } catch (err) {
      console.error("Checkout error:", err);
      setBuyError(err.message || "Unable to start checkout.");
    } finally {
      setBuyLoading(false);
    }
  }

  // ===== loading / error =====
  if (loading) {
    return (
      <main className="workout-detail-page py-5">
        <Container className="py-5 text-center">
          <Spinner animation="border" />
        </Container>
      </main>
    );
  }

  if (err || !plan) {
    return (
      <main className="workout-detail-page py-5">
        <Container className="py-5">
          {err && <Alert variant="danger">{err}</Alert>}
          {!plan && (
            <Alert variant="warning" className="mb-4">
              Workout not found.
            </Alert>
          )}
          <Button variant="secondary" onClick={() => navigate(-1)}>
            ← Go back
          </Button>
        </Container>
      </main>
    );
  }

  const cover = resolveCoverUrl(plan.coverUrl);
  const priceStr =
    typeof plan.price === "number"
      ? `${plan.price} ${plan.currency || "EUR"}`
      : null;

  return (
    <main className="workout-detail-page py-5">
      <Container>
        <div className="wd-breadcrumb mb-3">
          <Link to="/workouts" className="wd-link">
            ← Back to Workouts
          </Link>
        </div>

        <Row className="g-4 align-items-start">
          <Col md={5} lg={4}>
            <Card className="shadow-sm">
              <Card.Img src={cover} alt={plan.title} className="wd-cover" />
            </Card>
          </Col>

          <Col md={7} lg={8}>
            <header className="wd-header">
              <h1 className="wd-title">{plan.title}</h1>
              {plan.subtitle && <p className="wd-subtitle">{plan.subtitle}</p>}
            </header>

            <div className="wd-badges mb-3">
              {plan.level && (
                <Badge pill className="wd-pill">
                  Level: {plan.level}
                </Badge>
              )}
              {plan.weeks && (
                <Badge pill className="wd-pill">
                  {plan.weeks} weeks
                </Badge>
              )}
              {plan.sessionsPerWeek && (
                <Badge pill className="wd-pill">
                  {plan.sessionsPerWeek} sessions/wk
                </Badge>
              )}
              {!available && (
                <Badge pill className="wd-pill muted">
                  Coming soon
                </Badge>
              )}
            </div>

            <div className="wd-cta card shadow-sm p-3 mb-4">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="wd-price-label">Price</div>
                  <div className="wd-price">{priceStr || "—"}</div>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  disabled={!available || buyLoading}
                  onClick={handleBuy}
                >
                  {buyLoading ? "Redirecting to payment…" : "Buy"}
                </Button>
              </div>
            </div>

            {buyError && <p className="text-danger">{buyError}</p>}

            <section className="wd-description">
              <h2 className="wd-h2 text-light">What awaits</h2>
              <p className="wd-text fs-4 fw-bolder">
                {plan.shortDescription ||
                  "This plan includes structured programming, clear progression, and guidance to help you achieve your goal."}
              </p>
            </section>
          </Col>
        </Row>
      </Container>

      {/* ===== CHECKOUT SUCCESS MODAL ===== */}
      <Modal
        show={checkoutSuccess}
        onHide={() => setCheckoutSuccess(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Payment successful 🎉</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p className="mb-2">Thank you for your purchase!</p>
          <p className="mb-0">
            Check out your workouts in your <strong>profile</strong> to download
            them.
          </p>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={() => navigate("/profile")}>
            Go to profile
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}
